import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Music, Heart, Globe, Youtube, ArrowRight } from 'lucide-react';

// Sarah's actual image from her website
const SARAH_IMAGE = "https://customer-assets.emergentagent.com/job_cf4caf13-b586-4cda-9c41-d59090cfa577/artifacts/w5ti8a3m_sarah_Capeles_hero_image.jpg";

export default function AboutPage() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Hero */}
      <section className="pt-32 pb-20 bg-charcoal relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(https://images.unsplash.com/photo-1702567568428-9b46a363a8e5?w=1920)`,
            }}
          />
        </div>
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <p className="font-lato text-gold-400 text-sm uppercase tracking-[0.3em] mb-2">
            {t('About', 'Acerca de')}
          </p>
          <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white mb-4">
            Sarah Capeles
          </h1>
          <p className="font-lato text-xl text-gray-400 max-w-2xl">
            {t(
              'A voice dedicated to glorifying God through inspirational Christian music for over 30 years.',
              'Una voz dedicada a glorificar a Dios a través de música cristiana inspiradora por más de 30 años.'
            )}
          </p>
        </div>
      </section>

      {/* Bio Section */}
      <section className="py-20" data-testid="bio-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gold-500/10 rounded-3xl" />
              <img 
                src={SARAH_IMAGE}
                alt="Sarah Capeles"
                className="relative rounded-2xl shadow-2xl w-full max-w-md mx-auto"
                data-testid="sarah-photo"
              />
              <div className="absolute -bottom-6 -right-6 bg-gold-500 p-6 rounded-xl shadow-xl">
                <p className="font-playfair text-3xl font-bold text-white">30+</p>
                <p className="font-lato text-white/90 text-sm">{t('Years Singing', 'Años Cantando')}</p>
              </div>
            </div>

            {/* Content */}
            <div>
              <h2 className="font-playfair text-4xl font-bold text-charcoal mb-6">
                {t('A Heart for Worship', 'Un Corazón para la Adoración')}
              </h2>
              <div className="space-y-4 font-lato text-gray-600 leading-relaxed">
                <p>
                  {t(
                    'Sarah Capeles is a dedicated Christian singer and worship leader who has been sharing the love of Jesus Christ through music for over three decades. Based in New Jersey, she is an active member of the Seventh-day Adventist Church and has committed her life to spreading the Gospel through song.',
                    'Sarah Capeles es una cantante cristiana dedicada y líder de adoración que ha estado compartiendo el amor de Jesucristo a través de la música por más de tres décadas. Radicada en Nueva Jersey, es miembro activa de la Iglesia Adventista del Séptimo Día y ha dedicado su vida a difundir el Evangelio a través del canto.'
                  )}
                </p>
                <p>
                  {t(
                    'Her bilingual ministry reaches both Spanish and English-speaking communities, bringing messages of hope, redemption, and the soon coming of our Lord. Sarah\'s music is characterized by its heartfelt lyrics and melodic compositions that draw listeners closer to God.',
                    'Su ministerio bilingüe alcanza a comunidades de habla hispana e inglesa, llevando mensajes de esperanza, redención y la pronta venida de nuestro Señor. La música de Sarah se caracteriza por sus letras sinceras y composiciones melódicas que acercan a los oyentes a Dios.'
                  )}
                </p>
                <p>
                  {t(
                    'With music available on all major streaming platforms including Spotify and YouTube, Sarah continues to touch hearts around the world with her inspirational music that lifts souls to the heavenly throne.',
                    'Con música disponible en todas las principales plataformas de streaming incluyendo Spotify y YouTube, Sarah continúa tocando corazones alrededor del mundo con su música inspiradora que eleva las almas al trono celestial.'
                  )}
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-gray-200">
                <a href="https://www.youtube.com/sfrodelly" target="_blank" rel="noopener noreferrer" className="text-center group" data-testid="about-youtube-link">
                  <Youtube className="w-6 h-6 text-gold-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <p className="font-playfair text-lg font-bold text-charcoal group-hover:text-gold-600 transition-colors">YouTube</p>
                  <p className="font-lato text-gray-500 text-sm">{t('Watch Videos', 'Ver Videos')}</p>
                </a>
                <div className="text-center">
                  <Music className="w-6 h-6 text-gold-500 mx-auto mb-2" />
                  <p className="font-playfair text-2xl font-bold text-charcoal">10</p>
                  <p className="font-lato text-gray-500 text-sm">{t('Albums', 'Álbumes')}</p>
                </div>
                <div className="text-center">
                  <Globe className="w-6 h-6 text-gold-500 mx-auto mb-2" />
                  <p className="font-playfair text-2xl font-bold text-charcoal">2</p>
                  <p className="font-lato text-gray-500 text-sm">{t('Languages', 'Idiomas')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scripture Section */}
      <section className="py-20 bg-cream" data-testid="scripture-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <Heart className="w-10 h-10 text-gold-500 mx-auto mb-6" />
            <blockquote className="font-playfair text-2xl md:text-3xl text-charcoal italic leading-relaxed mb-6">
              {t(
                '"I will sing of the mercies of the Lord forever; with my mouth will I make known your faithfulness to all generations."',
                '"Las misericordias del Señor cantaré perpetuamente; de generación en generación haré notoria tu fidelidad con mi boca."'
              )}
            </blockquote>
            <p className="font-lato text-gold-600 text-lg uppercase tracking-widest">
              — {t('Psalm 89:1', 'Salmo 89:1')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gold-500" data-testid="about-cta">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="font-playfair text-4xl font-bold text-white mb-4">
            {t('Book Sarah for Your Event', 'Reserva a Sarah para tu Evento')}
          </h2>
          <p className="font-lato text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {t(
              'Available for church services, evangelistic campaigns, concerts, and special events.',
              'Disponible para servicios de iglesia, campañas evangelísticas, conciertos y eventos especiales.'
            )}
          </p>
          <Link to="/contact" data-testid="book-now-btn">
            <Button className="bg-white text-gold-600 hover:bg-gray-100 font-lato text-lg uppercase tracking-wider px-12 py-6 rounded-none">
              {t('Contact Now', 'Contactar Ahora')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
